import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState, useTransition } from "react";
import { toast } from "sonner";
import { useLocale, useTranslations } from "use-intl";

import { CheckIcon } from "@/components/appIcons";
import { CountryFlag } from "@/components/country-flag";
import { useViewer } from "@/hooks/use-viewer";
import { setLocale } from "@/i18n/actions";
import { getMessagesForRoute } from "@/i18n/get-messages-for-route";
import { replaceRouteLocale } from "@/i18n/route-locale";
import { authClient } from "@/lib/auth-client";
import { invalidateViewerContext, patchViewerContextQueryData } from "@/lib/viewer-context";
import { VIEWER_LOCALES, type ViewerLocale } from "@garimpa/lib/viewerPreferences";

const LOCALE_CONFIG: Record<ViewerLocale, { name: string; flag: string }> = {
  en: { name: "English", flag: "US" },
  pt: { name: "Português", flag: "BR" },
  es: { name: "Español", flag: "ES" },
};

export function LanguageSelector() {
  const locale = useLocale() as ViewerLocale;
  const tError = useTranslations("globals.error");
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isPending, startTransition] = useTransition();
  const viewer = useViewer();
  const user = viewer.user;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelect(next: ViewerLocale) {
    if (next === locale) {
      setOpen(false);
      return;
    }
    setOpen(false);
    startTransition(async () => {
      try {
        await getMessagesForRoute(next);
      } catch {
        toast.error(tError("message"));
        return;
      }
      patchViewerContextQueryData(queryClient, {
        user: { language: next },
        preferences: { locale: next },
      });
      // Cookie must be written before invalidate, otherwise the re-run of
      // root `beforeLoad` still resolves to the previous locale.
      await setLocale({ data: next });
      if (user && !user.isAnonymous) {
        await authClient.updateUser({ language: next });
      }
      await invalidateViewerContext(queryClient);
      const location = router.state.location;
      const localizedPathname = replaceRouteLocale(location.pathname, next);
      if (localizedPathname) {
        const hash = location.hash
          ? `${location.hash.startsWith("#") ? "" : "#"}${location.hash}`
          : "";
        await router.navigate({
          href: `${localizedPathname}${location.searchStr ?? ""}${hash}`,
        });
        return;
      }
      await router.invalidate();
    });
  }

  const current = LOCALE_CONFIG[locale];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={isPending}
        className="relative after:absolute after:-inset-1.5 after:content-[''] flex items-center justify-center rounded-lg size-8 hover:opacity-80 transition-opacity"
      >
        <CountryFlag code={current.flag} label={current.name} />
      </button>

      {open && (
        <ul className="absolute right-0 bottom-full mb-1 w-max rounded-lg border border-border bg-card shadow-lg py-1 z-50">
          {VIEWER_LOCALES.map((l) => {
            const cfg = LOCALE_CONFIG[l];
            return (
              <li key={l}>
                <button
                  onClick={() => handleSelect(l)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-primary/10 transition-colors"
                >
                  <CountryFlag code={cfg.flag} decorative />
                  <span>{cfg.name}</span>
                  {l === locale && <CheckIcon className="text-base ml-auto" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
